/* eslint-disable react/forbid-prop-types */
import React from 'react';
import { MdLocationOn } from 'react-icons/md';
import ReactLoading from 'react-loading';

import PropTypes from 'prop-types';

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@material-ui/core';

import LocaleMessage from '~/components/LocaleMessage';

import { DarkDialog } from '../../../styles';
import {
    EmptyState,
    LoadingWrap,
    MapHint,
    MapRoot,
    MapStage,
    MapViewport,
    Pin,
} from './styles';

function toPercent(coord) {
    if (coord === undefined || coord === null || Number.isNaN(Number(coord))) {
        return null;
    }
    return (Number(coord) / 1000) * 100;
}

export default function Rc3RobotMapLocationDialog({
    open,
    onClose,
    onRefresh,
    loading,
    refreshing,
    location,
}) {
    const imageUrl = location?.map_layer?.file?.url || '';
    const currentPoint = location?.current_point || null;
    const points = Array.isArray(location?.points) ? location.points : [];
    const busy = loading || refreshing;

    function renderPins() {
        return points
            .map(point => {
                const x = toPercent(point?.position?.x);
                const y = toPercent(point?.position?.y);
                if (x === null || y === null) {
                    return null;
                }
                const highlighted = !!point.highlighted;
                return (
                    <Pin
                        key={point.id}
                        $x={x}
                        $y={y}
                        $highlighted={highlighted}
                    >
                        <span className="pin-label" title={point.name}>
                            {point.name}
                        </span>
                        <MdLocationOn
                            className="pin-icon"
                            size={highlighted ? 48 : 32}
                        />
                    </Pin>
                );
            })
            .filter(Boolean);
    }

    function renderBody() {
        if (loading) {
            return (
                <LoadingWrap>
                    <ReactLoading
                        type="bubbles"
                        color="#c8c8c8"
                        height={50}
                        width={50}
                    />
                </LoadingWrap>
            );
        }

        if (!location || !imageUrl) {
            return (
                <EmptyState>
                    <LocaleMessage msg="rc3.robot.data.view_map.empty" />
                </EmptyState>
            );
        }

        return (
            <MapRoot>
                <MapHint>
                    <p className="hint-text">
                        <LocaleMessage msg="rc3.robot.data.view_map.hint" />
                    </p>
                    {currentPoint?.name ? (
                        <span className="hint-point">{currentPoint.name}</span>
                    ) : null}
                </MapHint>
                <MapViewport>
                    <MapStage>
                        <img src={imageUrl} alt="" draggable={false} />
                        {renderPins()}
                    </MapStage>
                </MapViewport>
            </MapRoot>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            maxWidth="lg"
            fullWidth
        >
            <DarkDialog>
                <DialogTitle>
                    <LocaleMessage msg="rc3.robot.data.view_map.title" />
                </DialogTitle>
                <DialogContent>{renderBody()}</DialogContent>
                <DialogActions>
                    <Button
                        onClick={onRefresh}
                        color="primary"
                        disabled={busy || !imageUrl}
                    >
                        <LocaleMessage msg="rc3.robot.data.view_map.refresh" />
                    </Button>
                    <Button
                        onClick={onClose}
                        color="primary"
                        autoFocus
                        disabled={busy}
                    >
                        <LocaleMessage msg="button.close" />
                    </Button>
                </DialogActions>
            </DarkDialog>
        </Dialog>
    );
}

Rc3RobotMapLocationDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    refreshing: PropTypes.bool,
    location: PropTypes.object,
};

Rc3RobotMapLocationDialog.defaultProps = {
    open: false,
    loading: false,
    refreshing: false,
    location: null,
};
